import { clientAPISend } from '@1x2network/client_api_integration'
import { flow } from '@1x2network/flow'
import { isPaylineConfiguration } from '@1x2network/is_engine_communication'
import { Specification, Spine } from '@1x2network/pixi_assets'
import { MediumWinPopupSpec } from '@1x2network/pixi_dialogue'
import { slotStore } from '@1x2network/slot-ui'
import { State } from '@1x2network/state_controller'
import { produce } from 'immer'
import { valueIds } from '../../constants/valueIds'
import { AnyBaseGameModel, BaseGameToHoldAndWinModel, CoinShowerEvent, isBonusTrigger } from '../../model/BaseGameModel'
import { HoldAndWinToBaseModel } from '../../model/HoldAndWinModel'
import { SetupModel } from '../../model/SetupModel'
import { SpinModel } from '../../model/SpinModel'
import { applyHoldAndWinTransition } from '../../utils/applyBoardTransforms'
import { formatCurrency } from '../../utils/formatCurrency'
import { sortByRowThenReel } from '../../utils/positionTuple'
import { GameViews } from '../../views/GameViews'
import { initWinLines } from './initWinLines'
import { covertToValueSymbols } from '../../model/convertToValueSymbols'
import { slamStopHandler } from '@1x2network/duck_slot_game'
import { SignalConnection } from 'typed-signals'

/**
 * Game loop logic for the base state of the game
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class BaseState extends State<GameViews, AnyBaseGameModel | HoldAndWinToBaseModel, SetupModel> {
    public handshake(model: SetupModel): void {
        if ('results' in model) {
            const { results } = model
            const { reelSet, hawReelSet } = this.views
            // Prepare with value symbols, might be restarting with transition data
            reelSet.prepareSpin(covertToValueSymbols(results.board.reels))
            this.views.hwBackground.visible = false
            this.views.baseBackground.visible = true
            hawReelSet.visible = false
            hawReelSet.getTopContainer(0).visible = false
            hawReelSet.getTopContainer(1).visible = false
            hawReelSet.getTopContainer(2).visible = false

            if (isPaylineConfiguration(model.config)) {
                initWinLines(reelSet, this.views.winLines, model.config.lines)
            }
        }

        // Update jackpot widget values
        this.views.jackpotWidgets.updateValues(model.config.prizes)
    }

    public async introduceState(model: SetupModel): Promise<void> {
        const slotState = slotStore.getState()
        slotState.slamStop.setEnabled(false)
        slotState.console.setSpinning()
        await this.views.introductionAnimation.introduce()
        this.views.falsePersistence.setCurrentTier()
        this.views.idleHandler.setIdle()
    }

    public async waitForInput(interact: Promise<void>): Promise<object | void> {
        const slotState = slotStore.getState()
        this.views.idleHandler.setIdle()
        slotState.console.setIdle()
        this.views.excitement.startIdle()
        slotState.slamStop.setEnabled(true)
        slamStopHandler.reset()
        await interact
        slotState.message.clearMessage()
        this.views.excitement.startHype()
        this.views.winLines.stopIteration()
        this.views.idleHandler.setActive()
    }

    public async waitForRound() {
        slotStore.getState().console.setSpinning()
        await this.views.reelSet.startSpin()
    }

    public inState(model: SpinModel | SetupModel): boolean {
        return (
            model.state.phase.next === 'NORMAL' ||
            (model.state.phase.current === 'NORMAL' && model.state.phase.next === '')
        )
    }

    public async leaveState(model: BaseGameToHoldAndWinModel): Promise<void> {
        const { soundPlayer } = this.views
        soundPlayer.fadeBackground(0.3)
        flow.wait(3400).then(() => soundPlayer.fadeBackground(1))
        soundPlayer.playHawStart()
        soundPlayer.playTransitionIn()
        slotStore.getState().spins.updateSpins(0)
        slotStore.getState().slamStop.setEnabled(false)
        await this.views.transition.transitionIn()
        this.views.falsePersistence.reset()
        this.views.falsePersistence.setInitialBonus()
        this.views.reelSet.visible = false
        this.views.baseBackground.visible = false
        this.views.reelSet.topContainer.visible = false

        const reels = model.results.board.reels
        const transformEvent = model.results.wins.events?.find((event) => event.type === 'TRANSFORM')
        const mutatedSymbols = applyHoldAndWinTransition(reels, transformEvent)
        this.views.hawReelSet.prepareSpin(mutatedSymbols)
    }

    public async enterState(model: HoldAndWinToBaseModel): Promise<void> {
        const { reelSet, baseBackground } = this.views

        // show footer message of total win amount after bonus
        if (model.state.win.total.currency) {
            slotStore.getState().message.setMessage('returns', [formatCurrency(model.state.win.total.currency)])
        }

        this.views.excitement.setState('base')
        this.views.excitement.startIdle()

        reelSet.visible = true
        reelSet.topContainer.visible = true
        baseBackground.reset()
        baseBackground.visible = true
        baseBackground.playIdle()
        this.views.falsePersistence.reset()
        await flow.wait(200)
        this.views.soundPlayer.playTransitionOut()
        await this.views.transition.transitionOut()
        slotStore.getState().console.setIdle()
        clientAPISend.feature.end()
    }

    public async playRound(model: AnyBaseGameModel): Promise<void> {
        const { reelSet, eventManager, falsePersistence } = this.views
        eventManager.setEvents(model.results.wins.events)

        /** handle a coin shower in the game */
        const isTransforming = eventManager.peek()?.type === 'TRANSFORM'
        const transformEvent = eventManager.peek() as CoinShowerEvent
        // Handle the collection of symbols for false persistence
        const landConnection = reelSet.spinAnimator.signals.onLand.connect((index) => {
            const symbols = reelSet.fetcher.getAllSymbolsByReel(index)
            const triggerSymbols = symbols.filter((s) => valueIds.includes(s.id))
            if (!isTransforming) {
                triggerSymbols.forEach((symbol) => falsePersistence.collect(symbol))
            }
        })

        const spinInData = produce(model.results.board.reels, (draft) => {
            if (!isTransforming) {
                return
            }

            for (const transform of transformEvent.transforms) {
                const [reel, row] = transform.position
                draft[reel][row] = { id: -1 }
            }
        })

        if (isTransforming) {
            await this.handleCoinShower(eventManager.nextEvent() as CoinShowerEvent)
        } else {
            this.views.anticipationManager.initConnections(model)
        }

        const anticipate = this.views.anticipationManager.calculateAnticipation(model)
        const hasAnticipated = anticipate.some((a) => a > 0)

        let connection: SignalConnection | undefined
        if (!isTransforming && slamStopHandler.canSlamStop) {
            if (slamStopHandler.isSlamStopping) {
                flow.skipStart()
            } else {
                connection = slamStopHandler.onSlamStop.connect(() => flow.skipStart())
            }
        }

        await reelSet.stopSpin({
            symbols: spinInData,
            minDuration: window.clientAPI.regulatory.betLimit,
            anticipate,
        })

        if (hasAnticipated) {
            // Wait for tint tween to finish
            await flow.wait(200)
        }

        flow.skipStop()
        connection?.disconnect()
        landConnection.disconnect()

        if (isTransforming) {
            // Destroy coin shower symbols
            this.views.coinShower.reset()
            // Replace results symbols with transform symbols
            reelSet.replaceSymbols(transformEvent.transforms)
        }

        await this.showAllLines(model)
        await this.handlePopup(model)

        const wins = model.results.wins.symbol.map((win) => ({
            lineInfo: win,
            lineId: win.line?.id || 0,
            winningAssets: this.views.reelSet.fetcher
                .getSymbolsByPosition(win.positions)
                .map((symbol) => symbol.spine)
                .filter((s) => s) as Spine[],
            nonWinningAssets: this.views.reelSet.fetcher
                .getSymbolsNotInPositions(win.positions)
                .map((symbol) => symbol.spine)
                .filter((s) => s) as Spine[],
        }))

        if (isBonusTrigger(model)) {
            await flow.wait(100) // Wait for falsePersistence to collect symbols
            // Animate the scatter symbols
            const positions = reelSet.fetcher.getPositions(valueIds)
            const symbols = reelSet.fetcher.getSymbolsByPosition(positions)
            reelSet.promoteSymbolsByPosition(sortByRowThenReel(positions))
            this.views.soundPlayer.playScatterTrigger()
            const promise = this.views.baseBackground.playScatter()
            await flow.wait(100)
            symbols.map((symbol) => symbol.playWin())
            await promise
        } else {
            this.views.winLines.iterateLines(wins)
        }
    }

    private async handleCoinShower(event: CoinShowerEvent) {
        const showerAnimationData = event.transforms.map((transform) => {
            const { position } = transform
            const destination = this.views.reelSet.getSymbolPosition(position[0], position[1])
            return { destination, transform }
        })
        flow.wait(1600).then(() => {
            this.views.falsePersistence.reset()
        })
        await this.views.coinShower.animate(showerAnimationData)
    }

    /** animate all lines and symbols that have won */
    async showAllLines(model: AnyBaseGameModel) {
        if (model.state.phase.next === 'NORMAL') {
            // dont iterate if we are going to transition
            return
        }

        const winningPositions = model.results.wins.symbol.flatMap((win) => win.positions)
        const nonWinningSymbols = this.views.reelSet.fetcher.getSymbolsNotInPositions(winningPositions)
        const winningLines = model.results.wins.symbol.map((win) => win.line?.id).filter((id) => id !== undefined)
        const winningSymbols = this.views.reelSet.fetcher.getUniqueSymbolsByPosition(winningPositions)

        if (winningSymbols.length) {
            const winningAssets = winningSymbols.map((symbol) => symbol.spine).filter((s) => s) as Spine[]
            const nonWinningAssets = nonWinningSymbols.map((symbol) => symbol.spine).filter((s) => s) as Spine[]

            this.views.excitement.win()
            this.views.soundPlayer.playWin(winningSymbols.map((symbol) => symbol.id))
            await this.views.winLines.playOnce({
                winningAssets,
                nonWinningAssets,
                lineIds: winningLines,
                lineInfo: model.results.wins.symbol,
            })
        }
    }

    /** animate one of the popups based on win */
    async handlePopup(model: AnyBaseGameModel) {
        const bet = model.bet
        const win = model.state.win.total.currency
        const stake = bet.stake.currency

        // dont animate any popups if win below stake
        if (win >= stake) {
            if (win / stake >= 5) {
                await this.views.tieredPopup.animate(model.bet.stake.currency, win)
            } else {
                await this.views.mediumWinPopup.animate(win)
                // bonus is triggering, lets wait for the popup to completely finish
                if (isBonusTrigger(model)) {
                    const mediumWinSpec = Specification.get('mediumWinPopup') as MediumWinPopupSpec
                    await flow.wait(mediumWinSpec.fadeDelay + mediumWinSpec.fadeDuration)
                }
            }
        }

        // show win
        if (win) {
            slotStore.getState().message.setMessage('returns', [formatCurrency(win)])
        }
    }

    /** round has errored, recover the spin and cancel autoplay */
    roundError(): void {
        this.views.reelSet.cancelSpin()
        slotStore.getState().spins.updateSpins(0)
    }
}
